import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from './AppShell';
import { API_BASE_URL } from '../config/api';
import type { ApiResponse } from '../types/api';
import { saveAuth, type AuthResponse } from '../utils/auth';
import { signInStateHome } from '../utils/signInReturn';
import { wechatLoginLog } from '../utils/wechatLoginLog';
import { ChevronLeft } from 'lucide-react';
import './Login.css';
import './LoginMethod.css';

/**
 * 微信 OAuth 成功后由后端 302 到此页（带 ?code= 一次性兑换码或 ?error=）
 */
const WeChatLoginDone: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [msg, setMsg] = useState<string>('正在完成登录…');
  const [isErr, setIsErr] = useState(false);
  const doneRef = useRef(false);
  const abortedRef = useRef(false);

  const goBackSignIn = () => {
    abortedRef.current = true;
    navigate('/sign-in', { state: signInStateHome() });
  };

  useEffect(() => {
    const err = searchParams.get('error');
    if (err) {
      wechatLoginLog('done_page_error_param', { errorLen: err.length, errorPreview: err.slice(0, 80) });
      setIsErr(true);
      setMsg(decodeURIComponent(err));
      return;
    }
    const code = searchParams.get('code');
    if (!code) {
      wechatLoginLog('done_page_missing_code', { keys: Array.from(searchParams.keys()) });
      setIsErr(true);
      setMsg('缺少授权参数，请从微信登录入口重试');
      return;
    }
    if (doneRef.current) return;
    doneRef.current = true;

    wechatLoginLog('done_page_exchange_start', { exchangeCodeLen: code.length, apiBase: API_BASE_URL });

    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/auth/wechat/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        if (abortedRef.current) {
          wechatLoginLog('done_page_exchange_aborted');
          return;
        }
        const j = (await r.json()) as ApiResponse<AuthResponse>;
        wechatLoginLog('done_page_exchange_response', {
          httpOk: r.ok,
          httpStatus: r.status,
          success: Boolean(j.success),
          error: j.success ? undefined : j.error,
          errorCode: j.success ? undefined : (j as { errorCode?: string }).errorCode,
          userId: j.data?.user?.id,
        });
        if (j.success && j.data) {
          saveAuth(j.data);
          wechatLoginLog('done_page_navigate_home', { mode: 'full_reload' });
          // 整页进入首页，避免微信内置浏览器 / 授权回流场景下 SPA 路由不触发子树重挂，导致仍显示未登录
          window.location.replace(`${window.location.origin}/`);
          return;
        }
        setIsErr(true);
        setMsg(j.error || '兑换登录失败');
      } catch (e) {
        if (!abortedRef.current) {
          setIsErr(true);
          setMsg('网络错误');
          wechatLoginLog('done_page_exchange_exception', { err: e instanceof Error ? e.message : String(e) });
        }
      }
    })();
  }, [searchParams, navigate]);

  return (
    <AppShell centerBody>
      <div className="login-box">
        <div className="login-flow-back-row">
          <button type="button" className="login-method-back login-method-back--top" onClick={goBackSignIn}>
            <ChevronLeft size={18} strokeWidth={2.25} aria-hidden />
            <span>返回</span>
          </button>
        </div>
        <h2>微信登录</h2>
        <p className={`wechat-done-msg ${isErr ? 'wechat-done-err' : ''}`}>{msg}</p>
        {isErr ? (
          <button type="button" className="submit-button" style={{ marginTop: 16 }} onClick={() => navigate('/sign-in', { state: signInStateHome() })}>
            返回选择登录方式
          </button>
        ) : null}
      </div>
    </AppShell>
  );
};

export default WeChatLoginDone;
